import { z } from 'zod';
import {
  workbookDefinitionSchema,
  type WorkbookDefinition,
} from '@erp/shared-types';
import { decodeShared, encodeShared } from '@/lib/sharedJson';
import { currentNode, trimUndoTree, type UndoTree } from '@/lib/undoTree';
import { parseUndoTree } from '@/lib/undoTreeSchema';
import { sameDefinition } from './workbookHistory';
import { shareUnchanged } from './workbookProcessChanges';

// What a Workbook's tab keeps across a reload, as a spreadsheet reopens the
// sheet you were on: its open tabs, and its edit history while the server
// still holds the state the history ends at. Per browser tab (sessionStorage);
// storage that is full, blocked or missing only means nothing comes back.

/** The most steps of history kept across a reload; a longer one is not kept. */
export const WORKBOOK_SESSION_HISTORY_STEPS = 100;

const historyKey = (workbookId: string) => `workbook:${workbookId}:history`;
const tabsKey = (workbookId: string) => `workbook:${workbookId}:tabs`;

function read(key: string): unknown {
  try {
    const text = sessionStorage.getItem(key);
    return text === null ? null : JSON.parse(text);
  } catch {
    return null;
  }
}

/** Whether `value` was stored; when not (full or blocked), nothing stale is left. */
function write(key: string, value: unknown): boolean {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage is unavailable; nothing is kept.
    }
    return false;
  }
}

const tabsSchema = z.object({
  opened: z.array(z.string()).max(100),
  selected: z.string(),
  /** The one open tab still temporary: the next item opened takes its place. */
  preview: z.string().optional(),
});
export type WorkbookTabs = z.infer<typeof tabsSchema>;

export function saveWorkbookTabs(workbookId: string, tabs: WorkbookTabs): void {
  write(tabsKey(workbookId), tabs);
}

export function loadWorkbookTabs(workbookId: string): WorkbookTabs | null {
  const parsed = tabsSchema.safeParse(read(tabsKey(workbookId)));
  return parsed.success ? parsed.data : null;
}

const noticeKey = (workbookId: string) =>
  `workbook:${workbookId}:history-notice`;
/** Stored histories share every part their steps share (see `encodeShared`). */
const HISTORY_FORMAT = 2;

/** How much of the edit history a reload kept, when it couldn't keep all of it. */
export type WorkbookHistoryNotice = { kept: number; dropped: number };
const noticeSchema = z.object({
  kept: z.number().int().nonnegative(),
  dropped: z.number().int().positive(),
});

/**
 * Keeps the history, with the Revision the server was at when it was kept.
 * Its steps share what they didn't change, so it is stored with each shared
 * part once. Past its cap of steps, or past what storage holds, the newest
 * steps that fit are kept and the rest dropped — and that is recorded, so
 * the reload says how much of the history came back.
 */
export function saveWorkbookHistory(
  workbookId: string,
  revision: number,
  tree: UndoTree<WorkbookDefinition>,
): void {
  const store = (kept: UndoTree<WorkbookDefinition>) =>
    write(historyKey(workbookId), {
      revision,
      format: HISTORY_FORMAT,
      tree: encodeShared(kept),
    });
  let size = Math.min(tree.nodes.length, WORKBOOK_SESSION_HISTORY_STEPS);
  let kept = 0;
  for (;;) {
    if (store(trimUndoTree(tree, size))) {
      kept = size;
      break;
    }
    if (size === 1) break;
    size = Math.max(1, Math.floor(size / 2));
  }
  const dropped = tree.nodes.length - kept;
  if (dropped > 0) write(noticeKey(workbookId), { kept, dropped });
  else
    try {
      sessionStorage.removeItem(noticeKey(workbookId));
    } catch {
      // Storage is unavailable.
    }
}

/** What the last save of the history couldn't keep, once: reading it clears it. */
export function takeWorkbookHistoryNotice(
  workbookId: string,
): WorkbookHistoryNotice | null {
  const parsed = noticeSchema.safeParse(read(noticeKey(workbookId)));
  try {
    sessionStorage.removeItem(noticeKey(workbookId));
  } catch {
    // Storage is unavailable.
  }
  return parsed.success ? parsed.data : null;
}

/**
 * The history kept for this Workbook, if the server is still at the Revision
 * it was kept at and the Definition opened is the one it ends at; else null,
 * since undoing to states of another Revision would overwrite newer work.
 */
export function loadWorkbookHistory(
  workbookId: string,
  revision: number,
  definition: WorkbookDefinition,
): UndoTree<WorkbookDefinition> | null {
  const stored = z
    .object({
      revision: z.number().int(),
      format: z.number().int().optional(),
      tree: z.unknown(),
    })
    .safeParse(read(historyKey(workbookId)));
  if (!stored.success || stored.data.revision !== revision) return null;
  const tree = parseUndoTree(
    stored.data.format === HISTORY_FORMAT
      ? decodeShared(stored.data.tree)
      : stored.data.tree,
    workbookDefinitionSchema,
  );
  if (!tree || !sameDefinition(currentNode(tree).state, definition))
    return null;
  // Parsed afresh, the steps no longer share what they didn't change; share
  // it with the Definition opened again, so comparing and storing them stays
  // cheap.
  return {
    ...tree,
    nodes: tree.nodes.map((node) => ({
      ...node,
      state: shareUnchanged(definition, node.state),
    })),
  };
}
